export {
  validateEmail,
  checkPasswordMessage,
  goodPassword,
} from "./validation";

// Translate backend error messages to Chinese
export const translateAuthError = (error, apiError) => {
  // Check for structured error response first
  if (apiError?.response?.data) {
    const data = apiError.response.data;

    // Handle retry_after scenarios
    if (data.retry_after) {
      return {
        message: `请求过于频繁，请在 ${data.retry_after} 秒后再试`,
        retryAfter: data.retry_after,
        action: data.action
      };
    }

    // Handle daily limit - auto switch to password
    if (data.error_type === 'daily_limit_exceeded') {
      return {
        message: '今日验证码发送次数已达上限，请使用密码登录',
        switchToPassword: true
      };
    }

    // Handle CAPTCHA required
    if (data.captcha_required || data.action === 'present_captcha') {
      return {
        message: '需要进行安全验证',
        captchaRequired: true,
        retryAfter: data.retry_after
      };
    }
  }

  // Fallback to string matching
  const lowercased = error.toLowerCase();

  if (lowercased.includes("daily limit exceeded")) {
    return {
      message: "今日验证码发送次数已达上限，请使用密码登录",
      switchToPassword: true
    };
  }

  if (lowercased.includes("too many requests from this ip")) {
    return {
      message: "当前IP请求过于频繁，请稍后再试"
    };
  }

  if (lowercased.includes("too many requests") || lowercased.includes("too many attempts")) {
    return {
      message: "请求过于频繁，请稍后再试"
    };
  }

  if (lowercased.includes("invalid or expired verification code")) {
    return {
      message: "验证码无效或已过期"
    };
  }

  if (lowercased.includes("invalid verification code")) {
    return {
      message: "验证码错误"
    };
  }

  if (lowercased.includes("verification failed")) {
    return {
      message: "验证失败"
    };
  }

  if (lowercased.includes("user not found")) {
    return {
      message: "该邮箱尚未注册"
    };
  }

  if (lowercased.includes("invalid password")) {
    return {
      message: "密码错误"
    };
  }

  if (lowercased.includes("captcha required") || lowercased.includes("verification required")) {
    return {
      message: "需要进行安全验证",
      captchaRequired: true
    };
  }

  if (lowercased.includes("server error")) {
    return {
      message: "服务器错误，请稍后再试"
    };
  }

  if (lowercased.includes("failed to process request")) {
    return {
      message: "密码重置链接已发送，请检查您的邮箱。如需重新发送，请稍后再试"
    };
  }

  if (lowercased.includes("valid password reset link already exists")) {
    return {
      message: "密码重置链接已发送，请检查您的邮箱。如需重新发送，请稍后再试"
    };
  }

  // If no translation found, return original (might already be in Chinese)
  return { message: error };
};

export {
  serialize,
  deserialize,
  htmlToSlate,
  slateToHtml,
  sanitizeXmlText,
  normalizeSlateContent,
  getPendingImageUploadWarning,
} from "./serializing";

export {
  getPodcastSession,
  setPodcastSession,
  getToken,
  setToken,
  getUser,
  setUser,
} from "./localStorage";

export function formatDurationFromSecond(value) {
  const minute = Math.floor(value / 60);
  const secondLeft = Math.floor(value - minute * 60);
  return `${minute}:${secondLeft < 10 ? `0${secondLeft}` : secondLeft}`;
}

export function convertDurationToHHMMSS(duration) {
  const parts = duration.split(":").map(Number);
  if (parts.length !== 2 || parts[0] < 60) {
    return duration; // 如果不是 mm:ss 格式或者分钟数不大于60，直接返回原始值
  }

  const totalMinutes = parts[0];
  const seconds = parts[1];
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  // TODO: handle the episode duration whoes hours > 24
  return [
    hours.toString().padStart(2, "0"),
    minutes.toString().padStart(2, "0"),
    seconds.toString().padStart(2, "0"),
  ].join(":");
}

export const renderCustomContent = (content) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, "text/html");

  const renderNode = (node) => {
    switch (node.nodeName.toLowerCase()) {
      case "p":
        return (
          <p key={Math.random()}>
            {Array.from(node.childNodes).map(renderNode)}
          </p>
        );
      case "strong":
        return (
          <strong key={Math.random()}>
            {Array.from(node.childNodes).map(renderNode)}
          </strong>
        );
      case "a":
        return (
          <a
            href={node.getAttribute("href")}
            key={Math.random()}
            target="_blank"
            rel="noopener noreferrer"
          >
            {node.textContent}
          </a>
        );
      case "figure":
        return (
          <figure key={Math.random()}>
            {Array.from(node.childNodes).map(renderNode)}
          </figure>
        );
      case "img":
        return (
          <img
            src={node.src}
            alt={node.alt}
            key={Math.random()}
            style={{ maxWidth: "100%", height: "auto" }}
          />
        );
      case "#text":
        return node.textContent;
      default:
        return null;
    }
  };

  return Array.from(doc.body.childNodes).map(renderNode);
};

export const truncateContent = (content) => {
  const CONTENT_MAX_LENGTH = 100;
  const plainText = content.replace(/<[^>]+>/g, "");
  return plainText.length > CONTENT_MAX_LENGTH
    ? plainText.slice(0, CONTENT_MAX_LENGTH) + "..."
    : plainText;
};

export const renderContent = (content, isExpanded) => {
  if (isExpanded) {
    return renderCustomContent(content);
  }
  return <p>{truncateContent(content)}</p>;
};

/** 从单集标题里尽力提取期数（如 "Vol.48 …" / "第 12 期" / "48 …"）。 */
export function extractVol(title) {
  if (!title) return undefined;
  const s = String(title);
  const m =
    s.match(/(?:vol|ep)\.?\s*(\d{1,4})/i) ||
    s.match(/第\s*(\d{1,4})\s*[期集]/) ||
    s.match(/^(\d{1,4})[\s.、·|-]/);
  return m ? Number(m[1]) : undefined;
}

/** 日期统一为 2026/07/08 形式（设计规范）。 */
export function formatDateSlash(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}/${mm}/${dd}`;
}
